// ... existing code ...

import { supabase } from './supabase';

export interface NotificationPayload {
  project_id?: string | null;
  project_name?: string | null;
  notification_type:
    | 'progress'
    | 'issue'
    | 'inspection'
    | 'approval'
    | 'question'
    | 'task'
    | 'system'
    | 'payment'
    | 'update'
    | 'photo'
    | 'file'
    | 'schedule'
    | 'instruction'
    | 'instruction_reply'
    | 'client_update'
    | 'client_question'
    | 'change_order'
    | 'checklist';
  title: string;
  body: string;
  target_role: 'management' | 'subcontractor' | 'client';
  /** Optional: notify a specific user only */
  user_id?: string | null;
  /** Navigation path when tapped */
  link_path?: string | null;
  /** Optional: link to a specific record */
  target_type?: string | null;
  target_id?: string | null;
  /** Who created this notification */
  created_by?: string | null;
  /** Deduplication key: table name of related entity */
  related_table?: string | null;
  /** Deduplication key: id of related entity */
  related_id?: string | null;
}

export async function createNotification(payload: NotificationPayload): Promise<{ success: boolean; deduplicated?: boolean; error?: string }> {
  try {
    // 1. Create the in-app notification
    const { data, error } = await supabase.functions.invoke('jphph-create-notification', {
      body: {
        project_id: payload.project_id ?? null,
        project_name: payload.project_name ?? null,
        notification_type: payload.notification_type,
        title: payload.title,
        body: payload.body,
        target_role: payload.target_role,
        user_id: payload.user_id ?? null,
        link_path: payload.link_path ?? null,
        target_type: payload.target_type ?? null,
        target_id: payload.target_id ?? null,
        created_by: payload.created_by ?? null,
        related_table: payload.related_table ?? null,
        related_id: payload.related_id ?? null,
      },
    });

    if (error) {
      console.error('[createNotification] edge function error:', error.message, payload);
      return { success: false, error: error.message };
    }

    if (!data?.success) {
      console.error('[createNotification] edge function returned failure:', data, payload);
      return { success: false, error: data?.error || 'Unknown error' };
    }

    // 2. Populate email queue and send emails to all recipients (fire-and-forget)
    const notificationId = data?.notification?.id as string | undefined;
    if (notificationId) {
      supabase.functions.invoke('jphph-populate-email-queue', {
        body: { notification_id: notificationId },
      }).catch((emailErr) => {
        // Log only — email failure is non-critical and won't block UI
        console.warn('[createNotification] email queue populate failed (non-critical):', emailErr);
      });
    }

    return { success: true, deduplicated: data.deduplicated };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[createNotification] unexpected error:', err, payload);
    return { success: false, error: message };
  }
}

/** Create multiple notifications at once (e.g. notify all roles) */
export async function createNotifications(payloads: NotificationPayload[]): Promise<void> {
  for (const p of payloads) {
    // eslint-disable-next-line no-await-in-loop
    await createNotification(p);
  }
}